import React from "react";
import { Button, Card, Empty, Spin, Typography } from "antd";

const { Text, Title } = Typography;

export type PageStateKind = "loading" | "empty" | "error" | "forbidden" | "plan-limit";

interface PageStateProps {
  kind: PageStateKind;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  traceId?: string;
  compact?: boolean;
}

const DEFAULT_COPY: Record<PageStateKind, { title: string; description: string }> = {
  loading: { title: "正在加载", description: "正在读取最新数据，请稍候。" },
  empty: { title: "暂无数据", description: "当前范围内还没有可展示的内容。" },
  error: { title: "加载失败", description: "服务暂时不可用，请稍后重试。" },
  forbidden: { title: "没有访问权限", description: "当前账号不能查看这个项目或功能。" },
  "plan-limit": { title: "当前套餐不可用", description: "升级套餐或等待额度重置后可继续使用。" },
};

const PageState: React.FC<PageStateProps> = ({
  kind,
  title,
  description,
  actionLabel,
  onAction,
  traceId,
  compact = false,
}) => {
  const copy = DEFAULT_COPY[kind];
  const interruptive = kind === "error" || kind === "forbidden" || kind === "plan-limit";
  const content = kind === "loading"
    ? <Spin size={compact ? "default" : "large"} />
    : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />;

  return (
    <Card
      className="hm-page-state"
      data-kind={kind}
      data-layout="responsive"
      role={interruptive ? "alert" : "status"}
      aria-live={interruptive ? "assertive" : "polite"}
      style={{
        width: "100%",
        maxWidth: 560,
        margin: compact ? "8px auto" : "24px auto",
        padding: compact ? 8 : 16,
        textAlign: "center",
      }}
    >
      {content}
      <Title level={compact ? 5 : 4} style={{ marginTop: 12, marginBottom: 4 }}>{title || copy.title}</Title>
      <Text type="secondary">{description || copy.description}</Text>
      {traceId && <Text type="secondary" style={{ display: "block", marginTop: 8 }}>追踪 ID：{traceId}</Text>}
      {actionLabel && onAction && (
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={onAction}>{actionLabel}</Button>
        </div>
      )}
    </Card>
  );
};

export default PageState;
